import React, { useEffect, useRef, useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { projects } from "../data/content";
import { ArrowSticker, Starburst, TinyStar } from "../components/Stickers";

export default function ProjectDetail() {
  const { slug } = useParams();
  const project = projects.find((p) => p.slug === slug);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [slug]);

  if (!project) {
    return <Navigate to="/" replace />;
  }

  const idx = projects.findIndex((p) => p.slug === slug);
  const next = projects[(idx + 1) % projects.length];

  return (
    <main data-testid="project-detail-page" className="relative">
      {/* Top crumb */}
      <div className="mx-auto max-w-[1400px] px-5 md:px-10 lg:px-14 pt-10">
        <Link
          to="/#projects"
          data-testid="project-back-link"
          className="inline-flex items-center gap-2 text-sm font-medium text-[color:var(--charcoal)] hover:text-[color:var(--pink)] transition-colors"
        >
          → חזרה לכל העבודות
        </Link>
      </div>

      {/* HERO */}
      <section
        data-testid="project-hero"
        className="mx-auto max-w-[1400px] px-5 md:px-10 lg:px-14 pt-10 md:pt-16 pb-16 md:pb-24"
      >
        <div className="flex items-center gap-3 mb-6">
          <span className="tag-pill">{project.category}</span>
          <span className="text-sm text-[color:var(--charcoal)]">
            תפקיד · {project.role}
          </span>
        </div>

        <motion.h1
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          data-testid="project-title"
          className="font-display font-black tracking-tight leading-[0.88] text-[15vw] md:text-[12vw] lg:text-[10vw] mb-12"
        >
          {project.title}
          <span className="text-[color:var(--pink)]">.</span>
        </motion.h1>

        {/* Cover */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="relative aspect-[16/9] md:aspect-[16/8] rounded-3xl overflow-hidden ink-border"
        >
          {project.cover ? (
            <img
              src={project.cover}
              alt={project.title}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full bg-[color:var(--pink)] flex items-center justify-center">
              <span className="font-display font-black text-[22vw] md:text-[14vw] text-[color:var(--paper)] leading-none">
                {project.title}
              </span>
            </div>
          )}
          <Starburst
            className="absolute -top-6 -left-6 w-16 h-16 hidden md:block"
            color="#FF1493"
          />
        </motion.div>
      </section>

      {/* CONTENT */}
      <section className="mx-auto max-w-[1400px] px-5 md:px-10 lg:px-14 pb-16 md:pb-24">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-7">
          <Block
            number="01"
            title="החלק שלי בפאזל"
            text={project.myPart}
            testId="project-section-mypart"
          />
          <Block
            number="02"
            title="האתגר"
            text={project.challenge}
            testId="project-section-challenge"
          />
          <Block
            number="03"
            title="הפיצוח ומה רואים"
            text={project.solution}
            testId="project-section-solution"
          />
        </div>
      </section>

      {/* GALLERY */}
      <section
        data-testid="project-gallery"
        className="mx-auto max-w-[1400px] px-5 md:px-10 lg:px-14 pb-24"
      >
        <div className="flex items-center gap-3 mb-8">
          <h3 className="font-display font-black text-3xl md:text-5xl">
            גלריה
          </h3>
          <TinyStar className="w-5 h-5" color="#FF1493" />
        </div>

        <Gallery
          cover={project.cover}
          title={project.title}
          images={project.gallery}
          galleryStyle={project.galleryStyle}
        />
      </section>

      {/* Next project */}
      <section className="border-t border-black/10">
        <Link
          to={`/projects/${next.slug}`}
          data-testid="project-next-link"
          className="mx-auto max-w-[1400px] px-5 md:px-10 lg:px-14 py-16 md:py-24 flex flex-col md:flex-row items-start md:items-center justify-between gap-6 group"
        >
          <div>
            <p className="text-sm tracking-widest uppercase text-[color:var(--charcoal)] mb-2">
              הפרויקט הבא
            </p>
            <h3 className="font-display font-black text-5xl md:text-7xl tracking-tight leading-none group-hover:text-[color:var(--pink)] transition-colors">
              {next.title}
            </h3>
          </div>
          <ArrowSticker
            className="w-24 h-16 group-hover:translate-x-[-12px] transition-transform"
            color="#FF1493"
          />
        </Link>
      </section>
    </main>
  );
}

function Block({ number, title, text, testId }) {
  return (
    <div
      data-testid={testId}
      className="ink-border rounded-2xl p-6 md:p-7 bg-[color:var(--paper)] flex flex-col gap-4 h-full"
    >
      <div className="flex items-baseline gap-3">
        <span className="font-display font-black text-5xl md:text-6xl text-[color:var(--pink)] leading-none">
          {number}
        </span>
        <h2 className="font-display font-black text-2xl md:text-3xl leading-tight">
          {title}
        </h2>
      </div>
      <p className="text-base md:text-lg leading-[1.7] text-[color:var(--ink)] mt-2">
        {text}
      </p>
    </div>
  );
}

function Gallery({ cover, title, images, galleryStyle }) {
  const list = images && images.length > 0 ? images : null;
  if (!list) {
    // Fallback when no gallery images: show single big cover
    return (
      <div className="rounded-2xl overflow-hidden ink-border aspect-[16/9] bg-[color:var(--warm)]">
        {cover ? (
          <img
            src={cover}
            alt={title}
            className="w-full h-full object-cover"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full bg-[color:var(--pink)] flex items-center justify-center text-[color:var(--paper)] font-display font-black text-5xl">
            {title.slice(0, 2)}
          </div>
        )}
      </div>
    );
  }

  // Newsletter style: tall portrait cards, scrollable on hover, click opens PDF
  if (galleryStyle === "newsletter") {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-5 md:gap-6">
        {list.map((item, i) => (
          <a
            key={i}
            href={item.pdf || "#"}
            target="_blank"
            rel="noopener noreferrer"
            className="group block relative rounded-2xl overflow-hidden ink-border bg-[color:var(--warm)] aspect-[3/4]"
          >
            <div className="absolute inset-0 overflow-hidden">
              <img
                src={item.image}
                alt={item.label || `${title} - ${i + 1}`}
                className="w-full object-cover object-top transition-transform duration-[3000ms] ease-linear group-hover:-translate-y-[calc(100%-100%/3)]"
                style={{ minHeight: "100%" }}
                loading="lazy"
              />
            </div>
            <div className="absolute inset-x-0 bottom-0 p-4 md:p-5 bg-gradient-to-t from-black/85 via-black/40 to-transparent text-[color:var(--paper)] flex items-end justify-between">
              <div>
                <span className="block text-xs font-medium tracking-widest uppercase opacity-80">
                  לחיצה תפתח PDF
                </span>
                <span className="block font-display font-bold text-xl md:text-2xl mt-0.5">
                  {item.label}
                </span>
              </div>
              <ArrowSticker
                className="w-10 h-7 rotate-180 group-hover:translate-x-[-4px] transition-transform"
                color="#FDFCF8"
              />
            </div>
          </a>
        ))}
      </div>
    );
  }

  // Default: horizontal carousel with arrows.
  // Each card hover: scale 1.25 + image switches from object-cover to object-contain
  // so the user can read the full image.
  return <CarouselGallery list={list} title={title} />;
}

function CarouselGallery({ list, title }) {
  const scrollerRef = useRef(null);
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(true);
  const [lightboxIdx, setLightboxIdx] = useState(null);

  // Detect if list contains video URLs
  const isVideoUrl = (src) => /\.(mp4|webm|mov|m4v)(\?|$)/i.test(src);
  const hasVideos = list.some((src) => typeof src === "string" && isVideoUrl(src));
  // Card aspect: 9/16 (portrait) if videos, else square
  const cardAspectClass = hasVideos ? "aspect-[9/16]" : "aspect-square";
  // Card width sized so visible count is similar; narrower for portrait
  const cardWidthClass = hasVideos
    ? "w-[58%] sm:w-[36%] md:w-[22%] lg:w-[19%]"
    : "w-[78%] sm:w-[48%] md:w-[32%] lg:w-[28%]";

  const updateButtons = () => {
    const el = scrollerRef.current;
    if (!el) return;
    const sl = el.scrollLeft;
    const maxScroll = el.scrollWidth - el.clientWidth;
    const fromStart = Math.abs(sl);
    setCanPrev(fromStart > 8);
    setCanNext(fromStart < maxScroll - 8);
  };

  useEffect(() => {
    updateButtons();
    const el = scrollerRef.current;
    if (!el) return;
    const onScroll = () => updateButtons();
    el.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      el.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [list.length]);

  useEffect(() => {
    if (lightboxIdx === null) return;
    const onKey = (e) => {
      if (e.key === "Escape") setLightboxIdx(null);
      if (e.key === "ArrowLeft")
        setLightboxIdx((i) => Math.min(list.length - 1, i + 1));
      if (e.key === "ArrowRight") setLightboxIdx((i) => Math.max(0, i - 1));
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [lightboxIdx, list.length]);

  const scrollByCards = (direction) => {
    const el = scrollerRef.current;
    if (!el) return;
    const card = el.querySelector("[data-gallery-card]");
    const step = card ? card.offsetWidth + 20 : 320;
    el.scrollBy({ left: -direction * step, behavior: "smooth" });
  };

  return (
    <div className="relative">
      <div
        ref={scrollerRef}
        data-testid="gallery-carousel"
        className="flex gap-4 md:gap-6 overflow-x-auto snap-x snap-mandatory pb-4 pt-12 -mt-12 px-1"
        style={{ scrollbarWidth: "none" }}
      >
        <style>{`[data-testid='gallery-carousel']::-webkit-scrollbar { display: none; }`}</style>
        {list.map((src, i) => {
          const isVid = typeof src === "string" && isVideoUrl(src);
          return (
            <button
              key={i}
              data-gallery-card
              onClick={() => setLightboxIdx(i)}
              aria-label={
                isVid
                  ? `הפעל וידאו ${i + 1}`
                  : `פתח תמונה ${i + 1} בגודל מלא`
              }
              className={`shrink-0 snap-start ${cardWidthClass} ${cardAspectClass} relative cursor-zoom-in p-0 bg-transparent`}
            >
              <div className="group h-full w-full overflow-hidden rounded-2xl ink-border bg-[color:var(--warm)] transition-transform duration-300 origin-center relative hover:z-50 hover:scale-[1.25] hover:overflow-visible">
                {isVid ? (
                  <>
                    <video
                      src={src}
                      muted
                      loop
                      playsInline
                      preload="none"
                      className="w-full h-full object-cover bg-black"
                    />
                    {/* Play badge */}
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                      <div className="w-14 h-14 md:w-16 md:h-16 rounded-full bg-[color:var(--paper)]/90 flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                        <svg
                          width="22"
                          height="22"
                          viewBox="0 0 24 24"
                          fill="currentColor"
                          className="text-[color:var(--ink)] mr-[2px]"
                        >
                          <path d="M8 5v14l11-7z" />
                        </svg>
                      </div>
                    </div>
                  </>
                ) : (
                  <img
                    src={src}
                    alt={`${title} - ${i + 1}`}
                    className="w-full h-full object-cover transition-all duration-300 group-hover:object-contain group-hover:bg-[color:var(--paper)]"
                    loading="lazy"
                  />
                )}
              </div>
            </button>
          );
        })}
      </div>

      <button
        type="button"
        aria-label="הקודם"
        data-testid="gallery-prev"
        onClick={() => scrollByCards(-1)}
        disabled={!canPrev}
        className="absolute top-1/2 -translate-y-1/2 right-0 md:-right-2 z-30 w-12 h-12 md:w-14 md:h-14 rounded-full bg-[color:var(--ink)] text-[color:var(--paper)] flex items-center justify-center shadow-[3px_3px_0_0_var(--pink)] hover:bg-[color:var(--pink)] hover:shadow-[3px_3px_0_0_var(--ink)] transition-all disabled:opacity-30 disabled:pointer-events-none"
      >
        <ChevronRight size={26} strokeWidth={2.5} />
      </button>
      <button
        type="button"
        aria-label="הבא"
        data-testid="gallery-next"
        onClick={() => scrollByCards(1)}
        disabled={!canNext}
        className="absolute top-1/2 -translate-y-1/2 left-0 md:-left-2 z-30 w-12 h-12 md:w-14 md:h-14 rounded-full bg-[color:var(--ink)] text-[color:var(--paper)] flex items-center justify-center shadow-[3px_3px_0_0_var(--pink)] hover:bg-[color:var(--pink)] hover:shadow-[3px_3px_0_0_var(--ink)] transition-all disabled:opacity-30 disabled:pointer-events-none"
      >
        <ChevronLeft size={26} strokeWidth={2.5} />
      </button>

      <AnimatePresence>
        {lightboxIdx !== null && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            data-testid="gallery-lightbox"
            className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-4 md:p-10"
            onClick={() => setLightboxIdx(null)}
          >
            <button
              type="button"
              aria-label="סגור"
              data-testid="lightbox-close"
              onClick={(e) => {
                e.stopPropagation();
                setLightboxIdx(null);
              }}
              className="absolute top-4 left-4 md:top-6 md:left-6 z-[110] w-12 h-12 rounded-full bg-[color:var(--paper)] text-[color:var(--ink)] flex items-center justify-center hover:bg-[color:var(--pink)] hover:text-[color:var(--paper)] transition-colors"
            >
              <X size={24} strokeWidth={2.5} />
            </button>

            <div className="absolute top-6 right-6 z-[110] font-display font-bold text-[color:var(--paper)] text-lg md:text-xl">
              {lightboxIdx + 1} / {list.length}
            </div>

            {typeof list[lightboxIdx] === "string" &&
            isVideoUrl(list[lightboxIdx]) ? (
              <motion.video
                key={lightboxIdx}
                src={list[lightboxIdx]}
                controls
                autoPlay
                playsInline
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.25 }}
                onClick={(e) => e.stopPropagation()}
                className="max-w-full max-h-full rounded-lg shadow-2xl bg-black"
              />
            ) : (
              <motion.img
                key={lightboxIdx}
                src={list[lightboxIdx]}
                alt={`${title} - ${lightboxIdx + 1}`}
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.25 }}
                onClick={(e) => e.stopPropagation()}
                className="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
              />
            )}

            {list.length > 1 && (
              <>
                <button
                  type="button"
                  aria-label="הקודם"
                  data-testid="lightbox-prev"
                  onClick={(e) => {
                    e.stopPropagation();
                    setLightboxIdx((i) => Math.max(0, i - 1));
                  }}
                  disabled={lightboxIdx === 0}
                  className="absolute top-1/2 -translate-y-1/2 right-4 md:right-8 z-[110] w-14 h-14 rounded-full bg-[color:var(--paper)] text-[color:var(--ink)] flex items-center justify-center hover:bg-[color:var(--pink)] hover:text-[color:var(--paper)] transition-colors disabled:opacity-30"
                >
                  <ChevronRight size={28} strokeWidth={2.5} />
                </button>
                <button
                  type="button"
                  aria-label="הבא"
                  data-testid="lightbox-next"
                  onClick={(e) => {
                    e.stopPropagation();
                    setLightboxIdx((i) => Math.min(list.length - 1, i + 1));
                  }}
                  disabled={lightboxIdx === list.length - 1}
                  className="absolute top-1/2 -translate-y-1/2 left-4 md:left-8 z-[110] w-14 h-14 rounded-full bg-[color:var(--paper)] text-[color:var(--ink)] flex items-center justify-center hover:bg-[color:var(--pink)] hover:text-[color:var(--paper)] transition-colors disabled:opacity-30"
                >
                  <ChevronLeft size={28} strokeWidth={2.5} />
                </button>
              </>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
